import { v4 as uuidv4 } from 'uuid';
import { db } from '@/db';
import type { UploadQueueRow } from '@/db';
import { updateStatus } from './queue';
import { getSupabase } from '@/auth/supabase-client';
import { emitAndWait, EmitTimeoutError } from '@/lib/events/emit-and-wait';
import { EVENT, WANDERO_BUCKET } from '@wandero/shared';
import type { UploadProcessedPayload } from '@wandero/shared';

const MAX_RETRIES = 3;
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB

/**
 * Marker for errors that must NOT trigger an automatic retry — typically
 * caused by user input or persistent server state (duplicate ids, bad mime,
 * bucket permission). The user can manually retry from the UI if needed.
 */
class TerminalUploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TerminalUploadError';
  }
}

/**
 * Marker for errors classified as transient (network-class) storage-upload
 * failures. These auto-retry via the same bounded-backoff path as
 * EmitTimeoutError.
 */
class TransientUploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TransientUploadError';
  }
}

// Network-class signals — a fetch/connection-level or 5xx failure that is
// very likely to succeed on retry (browser dropped the connection, gateway
// hiccup, etc).
const TRANSIENT_PATTERNS: RegExp[] = [
  /failed to fetch/i,
  /network/i,
  /load failed/i,
  /timed?\s*out/i,
  /connection/i, // reset / closed / aborted
  /\b5\d{2}\b/, // 500, 502, 503, 504, ...
  /server error/i,
  /bad gateway/i,
  /service unavailable/i,
  /gateway timeout/i,
];

// 4xx-class / user-input / persistent-state signals — retrying will not help.
const TERMINAL_PATTERNS: RegExp[] = [
  /duplicate/i,
  /already exists/i,
  /mime/i,
  /unsupported/i,
  /permission/i,
  /unauthorized/i,
  /forbidden/i,
  /not allowed/i,
  /\b413\b/,
  /too large/i,
  /payload too large/i,
  /\b400\b/,
  /bad request/i,
  /invalid/i,
];

/**
 * Classify a raw storage-upload error message as 'transient' (network-class,
 * safe to auto-retry) or 'terminal' (user-input / persistent-state, retry
 * won't help).
 *
 * Default for unmatched messages is 'terminal' — this is a conservative
 * choice: we only PROMOTE known network-class signals to 'transient', we
 * never silently auto-retry an error class we don't recognize.
 */
export function classifyStorageError(message: string): 'transient' | 'terminal' {
  if (TERMINAL_PATTERNS.some((re) => re.test(message))) return 'terminal';
  if (TRANSIENT_PATTERNS.some((re) => re.test(message))) return 'transient';
  return 'terminal';
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

let running = false;
let getTokenFn: (() => string | null) | null = null;

/**
 * Start the upload poller. Safe to call multiple times — only one loop runs.
 */
export function startUploader(getAuthToken: () => string | null): void {
  if (running) return;
  running = true;
  getTokenFn = getAuthToken;
  loop().finally(() => {
    running = false;
    getTokenFn = null;
  });
}

/**
 * Stop the upload poller.
 */
export function stopUploader(): void {
  running = false;
}

/**
 * Exported for test purposes — returns whether the uploader is currently running.
 */
export function isUploaderRunning(): boolean {
  return running;
}

// Suppress unused warning — getTokenFn retained for future auth header needs
void getTokenFn;

async function loop(): Promise<void> {
  while (running) {
    const next = await db.uploadQueue.where('status').equals('queued').first();
    if (!next) {
      await sleep(1000);
      continue;
    }
    await processOne(next);
  }
}

export async function processOne(row: UploadQueueRow): Promise<void> {
  await updateStatus(row.id, { status: 'uploading', progress: 0 });

  try {
    // Pre-checks — terminal: retry won't change the file.
    if (!row.file.type.startsWith('image/')) {
      throw new TerminalUploadError(`Unsupported file type: ${row.file.type}`);
    }
    if (row.file.size > MAX_FILE_SIZE) {
      throw new TerminalUploadError(
        `File too large: ${(row.file.size / 1024 / 1024).toFixed(1)} MB (max 100 MB)`,
      );
    }

    const sb = getSupabase();
    const ext = row.fileName.split('.').pop()?.toLowerCase() ?? 'jpg';
    const storagePath = `wps/${uuidv4()}.${ext}`;

    // 1. Storage upload
    const { error: upErr } = await sb.storage
      .from(WANDERO_BUCKET)
      .upload(storagePath, row.file, {
        contentType: row.file.type,
        upsert: false,
      });
    if (upErr) {
      const classification = classifyStorageError(upErr.message);
      const wrappedMessage = `Storage upload failed: ${upErr.message}`;
      throw classification === 'transient'
        ? new TransientUploadError(wrappedMessage)
        : new TerminalUploadError(wrappedMessage);
    }

    await updateStatus(row.id, { status: 'processing', progress: 50, uploadedPath: storagePath });

    // 2. Emit + wait for worker response
    type RequestPayload = {
      storage_path: string;
      original_filename: string;
      content_type: string;
      size_bytes: number;
      auto_ai_pipeline: boolean;
      lang_code: string;
      override: boolean;
      category_assignments?: Array<{ category_id: number; is_primary: boolean }>;
    };

    const requestPayload: RequestPayload = {
      storage_path: storagePath,
      original_filename: row.fileName,
      content_type: row.file.type,
      size_bytes: row.file.size,
      auto_ai_pipeline: row.options.auto_ai_pipeline ?? true,
      lang_code: row.options.lang_code ?? row.options.langCode ?? 'pl',
      override: row.options.override ?? false,
    };
    if (row.options.category_assignments) {
      requestPayload.category_assignments = row.options.category_assignments;
    }

    const result = await emitAndWait<RequestPayload, UploadProcessedPayload>(
      EVENT.UPLOAD_PROCESS_REQUESTED,
      requestPayload,
      EVENT.UPLOAD_PROCESSED,
      { timeoutMs: 60_000 },
    );

    if ('error' in result) {
      // Worker rejected (duplicate legacy id, validation, etc.) — retry won't help.
      throw new TerminalUploadError(result.error);
    }

    await updateStatus(row.id, {
      status: 'done',
      progress: 100,
      wallpaper_id: result.wallpaper_id,
    });
  } catch (err: unknown) {
    const errorMsg = err instanceof Error ? err.message : String(err);
    const isTransient = err instanceof EmitTimeoutError || err instanceof TransientUploadError;
    const retries = row.retries + 1;

    // Transient failures (worker timeout, network-class storage errors)
    // auto-retry up to MAX_RETRIES. Validation, terminal storage, and
    // worker-rejected errors fail fast — the user retries manually via UI.
    if (isTransient && retries <= MAX_RETRIES) {
      await updateStatus(row.id, { status: 'queued', retries, error: errorMsg });
      await sleep(2000 * retries * 2);
    } else {
      await updateStatus(row.id, { status: 'error', error: errorMsg, retries });
    }
  }
}
